import { createContext, useCallback, useContext, useState, type ReactNode } from 'react';
import { Alert, Snackbar } from '@mui/material';
import { errorMessage } from '../api/client';
import { isApiError } from '../api/client';

type Sev = 'success' | 'error' | 'info' | 'warning';
type Notify = (msg: string, sev?: Sev) => void;
const Ctx = createContext<Notify>(() => {});

export function NotifyProvider({ children }: { children: ReactNode }) {
  const [msg, setMsg] = useState<{ text: string; sev: Sev; key: number } | null>(null);
  const notify = useCallback<Notify>((text, sev = 'success') => setMsg({ text, sev, key: Date.now() }), []);
  return (
    <Ctx.Provider value={notify}>
      {children}
      <Snackbar
        key={msg?.key}
        open={!!msg}
        autoHideDuration={msg?.sev === 'error' ? 8000 : 4000}
        onClose={(_, reason) => reason !== 'clickaway' && setMsg(null)}
        anchorOrigin={{ vertical: 'bottom', horizontal: 'center' }}
      >
        {msg ? (
          <Alert severity={msg.sev} variant="filled" onClose={() => setMsg(null)} sx={{ maxWidth: 560 }}>
            {msg.text}
          </Alert>
        ) : undefined}
      </Snackbar>
    </Ctx.Provider>
  );
}

export function useNotify() {
  const notify = useContext(Ctx);
  return {
    ok: (m: string) => notify(m, 'success'),
    info: (m: string) => notify(m, 'info'),
    /** Errors from the API; a cancelled 2FA prompt is silent. */
    error: (e: unknown, prefix?: string) => {
      if (isApiError(e, 'totp_cancelled')) return;
      notify(prefix ? `${prefix}: ${errorMessage(e)}` : errorMessage(e), 'error');
    },
  };
}
